import React from "react";
import DrawerWrapper from "../drawer";
import Button from "../button";
import { isPermit } from "../../utils/permissionCheck";

interface DrawerLayoutProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  title?: string;
  children: React.ReactNode;
  width?: number | string;
  placement?: "top" | "right" | "bottom" | "left";
  closable?: boolean;
  extra?: React.ReactNode;
  footer?: React.ReactNode;
  onConfirm?: () => void;
  onRemoveRole?: () => void;
  cancelText?: string;
  footerLink?: boolean;
  footerLinkText?: string;
  canEditRole?: boolean;
  onCancel?: () => void;
  confirmText?: string;
  loading?: boolean;
  className?: string;
  isLoading?: boolean;
}

const DrawerLayout: React.FC<DrawerLayoutProps> = ({
  open,
  setOpen,
  title = "Drawer",
  children,
  width = 600,
  placement = "right",
  closable = true,
  extra,
  footer = true,
  onConfirm,
  onRemoveRole,
  footerLink = false,
  loading = false,
  onCancel,
  cancelText = "Cancel",
  footerLinkText = "remove role",
  confirmText = "Confirm",
  canEditRole,
  className,
}) => {
  const onClose = () => setOpen(false);
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  // inside DrawerLayout
  const defaultFooter = (
    <div
      className={`drawer-footer flex ${
        footerLinkText && isPermit(5, "delete") && onRemoveRole
          ? "justify-between"
          : "justify-end"
      } items-center`}
    >
      {/* Left side: delete link */}
      {isPermit(5, "delete") && onRemoveRole && (
        <Button
          className="ant-btn-tertiary transparent ant-btn-link"
          onClick={onRemoveRole}
        >
          {footerLinkText}
        </Button>
      )}

      {/* Right side: cancel + confirm */}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button className="ant-btn-tertiary" onClick={handleCancel}>
            {cancelText}
          </Button>
        )}
        {onConfirm && (
          <Button
            loading={loading}
            className="ant-btn-secondary"
            onClick={onConfirm}
          >
            {confirmText}
          </Button>
        )}
      </div>
    </div>
  );

  // Footer shows if any of these conditions are true
  const drawerFooter =
    footer ||
    onCancel || // ensures "View Permissions" button shows in view mode
    onConfirm ||
    onRemoveRole
      ? defaultFooter
      : null;

  return (
    <DrawerWrapper
      className={className}
      title={title}
      onClose={onClose}
      open={open}
      width={width}
      placement={placement}
      closable={closable}
      extra={extra}
      footer={footer ? drawerFooter : null}
    >
      {children}
    </DrawerWrapper>
  );
};

export default DrawerLayout;
